import React from 'react';
import PropTypes from 'prop-types';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { getColorForValue } from '../../Utils/ColorUtils';

// Helper function to get light gradient color based on score color
const getGradientColor = (scoreColor) => {
  const colorMap = {
    '#EF4444': 'rgba(239, 68, 68, 0.1)', // brand red for values <= 4
    '#F59E0B': 'rgba(245, 158, 11, 0.1)', // brand yellow for values 5-6
    '#7FE3D9': 'rgba(127, 227, 217, 0.15)', // light brand green for values 7-8
    '#33C6B6': 'rgba(51, 198, 182, 0.15)' // brand green for values 9-10
  };
  return colorMap[scoreColor] || 'rgba(107, 114, 128, 0.08)';
};

// Circular progress chart component
const CircularProgressChart = ({ value, maxValue = 10 }) => {
  // Parse value to ensure it's a number
  const numericValue = parseFloat(value) || 0;
  
  // Calculate the percentage
  const percentage = (numericValue / maxValue) * 100;
  
  // Create data for the pie chart
  const data = [
    { name: 'Value', value: percentage },
    { name: 'Remaining', value: 100 - percentage }
  ];
  
  // Get color based on value
  const color = getColorForValue(numericValue);
  
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-52 h-52">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              startAngle={90}
              endAngle={-270}
              innerRadius="60%"
              outerRadius="80%"
              dataKey="value"
              cornerRadius={5}
            >
              <Cell fill={color} />
              <Cell fill="#f3f4f6" />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-5xl font-bold" style={{ color: '#374151' }}>{numericValue}</div>
          <div className="text-lg text-gray-500 font-medium">/ {maxValue}</div>
        </div>
      </div>
    </div>
  );
};

// ScoreShard component for displaying two numerical values with circular charts
const ScoreShard = ({ 
  title, 
  primaryValue, 
  primaryLabel, 
  secondaryValue, 
  secondaryLabel, 
  maxValue = 10,
  rationale
}) => {
  // Get color for primary value
  const primaryColor = getColorForValue(parseFloat(primaryValue) || 0);
  const gradientColor = getGradientColor(primaryColor);
  
  return (
    <div 
      className="border border-gray-200 rounded-lg p-6 relative overflow-hidden"
      style={{
        background: `linear-gradient(135deg, #ffffff 0%, #ffffff 50%, ${gradientColor} 100%)`
      }}
    >
      <div className="flex gap-6 h-full">
        {/* Left Column - Charts */}
        <div className="flex flex-col justify-start items-start flex-shrink-0" style={{ maxWidth: '280px' }}>
          {/* Primary Chart */}
          <div className="flex flex-col items-center">
            <CircularProgressChart 
              value={primaryValue || 0}
              maxValue={maxValue}
            />
          </div>
          
          {/* Secondary Chart - Only show if secondaryValue is not null */}
          {secondaryValue !== null && (
            <div className="flex items-center space-x-1">
              <div className="relative w-20 h-20 flex-shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[
                        { value: (parseFloat(secondaryValue) || 0) * 10 },
                        { value: 100 - ((parseFloat(secondaryValue) || 0) * 10) }
                      ]}
                      cx="50%"
                      cy="50%"
                      startAngle={90}
                      endAngle={-270}
                      innerRadius="60%"
                      outerRadius="80%"
                      dataKey="value"
                      cornerRadius={2}
                    >
                      <Cell fill={getColorForValue(parseFloat(secondaryValue) || 0)} />
                      <Cell fill="#f3f4f6" />
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-xl font-bold" style={{ color: '#374151' }}>{secondaryValue || 0}</div>
                </div>
              </div>
              <div className="text-sm text-gray-700 font-medium flex-1">{secondaryLabel}</div>
            </div>
          )}
        </div>
        
        {/* Right Column - Title and Rationale */}
        <div className="flex flex-col justify-center">
          <h3 className="text-4xl font-bold text-gray-900 mb-4">{title}</h3>
          <p className="text-lg text-gray-700 leading-relaxed">
            {rationale || "No evaluation completed"}
          </p>
        </div>
      </div>
    </div>
  );
};

ScoreShard.propTypes = {
  title: PropTypes.string.isRequired,
  primaryValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  primaryLabel: PropTypes.string,
  secondaryValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  secondaryLabel: PropTypes.string,
  maxValue: PropTypes.number,
  rationale: PropTypes.string
};

export default ScoreShard;
